import { useCallback, useEffect, useRef, useState } from 'react';

import type { TenantResponse } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

type Status = 'idle' | 'loading' | 'success' | 'error';

/**
 * The tenant hierarchy the current principal can see (its home subtree). The
 * server returns descendants scoped by the caller's access, so this is the org
 * tree the user is allowed to manage.
 */
export function useTenants({ enabled = true }: { enabled?: boolean } = {}) {
  const apiClient = useApiClient();
  const [tenants, setTenants] = useState<TenantResponse[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();
  const inFlight = useRef(false);
  const loaded = useRef(false);

  const reload = useCallback(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (!loaded.current) setStatus('loading');
    apiClient
      .listTenants({ limit: '100' })
      .then((res) => {
        setTenants(res.data);
        loaded.current = true;
        setStatus('success');
        setError(undefined);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load tenants');
        if (!loaded.current) setStatus('error');
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [apiClient]);

  useEffect(() => {
    if (enabled) reload();
  }, [enabled, reload]);

  const createTenant = useCallback(
    async (body: { name: string; slug: string; parent_id?: string | null }) => {
      const t = await apiClient.createTenant(body);
      reload();
      return t;
    },
    [apiClient, reload],
  );

  const updateTenant = useCallback(
    async (id: string, body: { name?: string; settings?: Record<string, unknown> }) => {
      const t = await apiClient.updateTenant(id, body);
      reload();
      return t;
    },
    [apiClient, reload],
  );

  const reparentTenant = useCallback(
    async (id: string, parentId: string) => {
      const t = await apiClient.reparentTenant(id, parentId);
      reload();
      return t;
    },
    [apiClient, reload],
  );

  const deleteTenant = useCallback(
    async (id: string) => {
      await apiClient.deleteTenant(id);
      reload();
    },
    [apiClient, reload],
  );

  return { tenants, status, error, reload, createTenant, updateTenant, reparentTenant, deleteTenant };
}
